import { Link, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import {Menu, X, LogIn, LogOut, ShoppingBag} from 'lucide-react'
import { useCart } from '../contexts/CartContext'
import { motion, AnimatePresence } from 'framer-motion'
import { lumi } from '../lib/lumi'


const Header = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [user, setUser] = useState(lumi.auth.user)
  const [isLoading, setIsLoading] = useState(false)
  const location = useLocation()
  const { totalItems, openDrawer } = useCart()

  useEffect(() => {
    const unsubscribe = lumi.auth.onAuthChange(({ user: newUser }) => {
      setUser(newUser)
      setIsLoading(false)
    })
    return () => unsubscribe()
  }, [])

  const isAdmin = user?.userRole === 'ADMIN'

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
    { path: '/actualites', label: 'Events' },
    { path: '/press', label: 'Press' },
    ...(isAdmin ? [{ path: '/admin', label: 'Admin' }] : []),
  ]

  const isActive = (path: string) => location.pathname === path

  return (
    <header className="sticky top-0 left-0 right-0 z-50 bg-[#FAF9F6]/95 backdrop-blur-sm border-b border-[#D4C5B9]/30">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" className="hover:opacity-80 transition-opacity flex items-center flex-shrink-0 notranslate" translate="no" aria-label="Studio RocheColombe - Home">
          <img
            src="https://static.lumi.new/44/4495c6feb2104e9b3b6d9f89ff4c7729.webp"
            alt="Studio RocheColombe"
            className="h-12 sm:h-14 md:h-14 lg:h-16 w-auto object-contain"
            width="260"
            height="64"
          />
        </Link>

        {/* Desktop Navigation */}
        <ul className="hidden md:flex items-center justify-center flex-1 gap-5 lg:gap-7 xl:gap-9">
          {navLinks.map((link, idx) => (
            <li key={link.path} className="flex items-center gap-5 lg:gap-7 xl:gap-9">
              <Link
                to={link.path}
                className={`text-sm lg:text-base font-medium tracking-wide whitespace-nowrap transition-colors relative ${
                  isActive(link.path) ? 'text-[#8B7355]' : 'text-[#2C2C2C] hover:text-[#8B7355]'
                }`}
              >
                {link.label}
                {isActive(link.path) && (
                  <motion.div
                    layoutId="underline"
                    className="absolute -bottom-2 left-0 right-0 h-0.5 bg-[#8B7355]"
                  />
                )}
              </Link>
              {idx < navLinks.length - 1 && (
                <span className="text-[#D4C5B9] select-none" aria-hidden="true">•</span>
              )}
            </li>
          ))}
        </ul>

        {/* Mobile cart icon (always visible on mobile) */}
        <div className="flex md:hidden items-center gap-3">
          <button
            onClick={() => openDrawer()}
            className="relative flex items-center justify-center w-11 h-11 text-[#2C2C2C] hover:text-[#8B7355] transition-colors"
            aria-label={`Cart (${totalItems} ${totalItems === 1 ? 'item' : 'items'})`}
          >
            <ShoppingBag size={22} />
            {totalItems > 0 && (
              <span className="absolute top-1 right-1 w-5 h-5 bg-[#7F1734] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {totalItems}
              </span>
            )}
          </button>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center justify-center w-11 h-11 text-[#2C2C2C] hover:text-[#8B7355] transition-colors"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isOpen}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Cart & Auth (desktop) */}
        <div className="hidden md:flex items-center gap-4 flex-shrink-0">
          {/* Cart icon */}
          <button
            onClick={() => openDrawer()}
            className="relative flex items-center text-[#2C2C2C] hover:text-[#8B7355] transition-colors"
            title="Panier"
          >
            <ShoppingBag size={20} />
            {totalItems > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#7F1734] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {totalItems}
              </span>
            )}
          </button>
          {user ? (
            <button
              onClick={async () => {
                if (isLoading) return
                setIsLoading(true)
                try {
                  await lumi.auth.signOut()
                } catch (error) {
                  console.error('Error during logout:', error)
                  setIsLoading(false)
                }
              }}
              disabled={isLoading}
              className="flex items-center gap-2 text-sm font-medium text-[#2C2C2C] hover:text-[#8B7355] transition-colors disabled:opacity-50"
              title="Logout"
            >
              <LogOut size={20} />
            </button>
          ) : (
            <button
              onClick={async () => {
                if (isLoading) return
                setIsLoading(true)
                try {
                  await lumi.auth.signIn()
                } catch (error) {
                  console.error('Error during login:', error)
                  setIsLoading(false)
                }
              }}
              disabled={isLoading}
              aria-label="Login"
              className="flex items-center gap-2 text-sm font-medium text-[#2C2C2C] hover:text-[#8B7355] transition-colors disabled:opacity-50"
              title="Login"
            >
              <LogIn size={20} />
            </button>
          )}
        </div>

      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#FAF9F6] border-t border-[#D4C5B9]/30 overflow-hidden"
          >
            <ul className="px-4 sm:px-6 py-4 space-y-1">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center min-h-[56px] px-2 text-lg font-medium tracking-wide transition-colors ${
                      isActive(link.path) ? 'text-[#8B7355]' : 'text-[#2C2C2C] hover:text-[#8B7355]'
                    }`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li className="pt-2 border-t border-[#D4C5B9]/30">
                {user ? (
                  <button
                    onClick={async () => {
                      if (isLoading) return
                      setIsLoading(true)
                      try {
                        await lumi.auth.signOut()
                        setIsOpen(false)
                      } catch (error) {
                        console.error('Logout error:', error)
                        setIsLoading(false)
                      }
                    }}
                    disabled={isLoading}
                    className="flex items-center gap-2 text-base font-medium text-[#2C2C2C] hover:text-[#8B7355] transition-colors disabled:opacity-50"
                  >
                    <LogOut size={20} />
                    Logout
                  </button>
                ) : (
                  <button
                    onClick={async () => {
                      if (isLoading) return
                      setIsLoading(true)
                      try {
                        await lumi.auth.signIn()
                        setIsOpen(false)
                      } catch (error) {
                        console.error('Login error:', error)
                        setIsLoading(false)
                      }
                    }}
                    disabled={isLoading}
                    aria-label="Login"
                    className="flex items-center gap-2 text-base font-medium text-[#2C2C2C] hover:text-[#8B7355] transition-colors disabled:opacity-50"
                  >
                    <LogIn size={20} />
                    Login
                  </button>
                )}
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}

export default Header
